import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  AuthContext,
  AuthContextInterface,
  S3CredentialsContext,
  S3CredentialsContextInterface,
  UserDetailsContext,
  UserDetailsContextInterface,
} from '../../contexts';
import { FileState, GetProjectFilesResponse, ProjectFile } from '../../proto/api_pb';
import { awaitableUploadToS3 } from '../../utils/S3Utils';
import { getAllProjectFiles, postProjectFiles } from '../../services/projectService';
import useGetValidAccessToken from '../../hooks/useGetValidAccessToken';
import { FileStateReverseMap } from '../SingleProject/types';
import { ShareType } from '../../proto/api_pb';
import { toast } from 'react-toastify';
import { RpcError } from 'grpc-web';
import { BORDER_COLOR, TEXT_COLOR } from '../../theme';
import Mixpanel from '../../utils/mixpanel';
import { ProjectFile as ProjectFileComponent } from '../SingleProject/ProjectFile';
import Previewer from '../Modal/Previewer';
import { projectPageStyles } from '../../pages/Projects/styles';

const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

const VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
  'video/webm',
  'video/ogg',
  'video/mpeg',
  'video/3gpp',
  'video/3gpp2',
];

const isVideoFile = (file: File) =>
  file.type.startsWith('video/') || VIDEO_MIME_TYPES.includes(file.type);

const getPreviewType = (mimeType: string): 'image' | 'video' | 'pdf' | 'text' | 'other' => {
  if (!mimeType) return 'other';

  const normalized = mimeType.trim().toLowerCase();

  const imageTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/svg+xml',
    'image/tiff',
    'image/heic',
    'image/heif',
    '.jpeg',
    'jpeg',
    ';jpeg',
    '.jpg',
    'jpg',
    ';jpg',
    '.png',
    'png',
    ';png',
    '.gif',
    'gif',
    ';gif',
    '.webp',
    'webp',
    ';webp',
    '.bmp',
    'bmp',
    ';bmp',
    '.svg',
    'svg',
    ';svg',
    '.tiff',
    'tiff',
    ';tiff',
    '.heic',
    'heic',
    ';heic',
    '.heif',
    'heif',
    ';heif',
  ];

  if (imageTypes.includes(normalized)) return 'image';
  if (normalized.startsWith('image/')) return 'image';
  if (normalized.startsWith('video/')) return 'video';
  if (normalized === 'application/pdf') return 'pdf';
  if (normalized.startsWith('text/')) return 'text';

  return 'other';
};

interface VideoUploadProps {
  getStorageUsed: () => Promise<void>;
  isFilesUpdated?: boolean;
  setIsFilesUpdated?: (val: boolean) => void;
  setUploadingFilesDetailsList: React.Dispatch<React.SetStateAction<any[]>>;
  setIsUploading: (val: boolean) => void;
  setUploadingCardsStatusList: React.Dispatch<React.SetStateAction<any[]>>;
  setIsUploadProcessing: (val: boolean) => void;
  setIsCountUpdated: (val: boolean) => void;
  projectId?: string;
}

export const VideoUpload: React.FC<VideoUploadProps> = ({
  getStorageUsed,
  isFilesUpdated,
  setIsFilesUpdated,
  setUploadingFilesDetailsList,
  setIsUploading,
  setUploadingCardsStatusList,
  setIsUploadProcessing,
  setIsCountUpdated,
  projectId = '',
}) => {
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { enterprise } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const { getValidAccessToken } = useGetValidAccessToken();
  const [isLocalUploading, setIsLocalUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [videos, setVideos] = useState<ProjectFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedFileToPreview, setSelectedFileToPreview] = useState<ProjectFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { page, scrollContainerRef, setIsScrollEnabled, setPage } = useInfiniteScroll();

  const PAGE_SIZE = 20;

  const fetchVideos = useCallback(async (pageNum: number = 0, replace: boolean = false) => {
    setIsLoading(true);
    const token = await getValidAccessToken();
    const res = await getAllProjectFiles(
      token,
      '',
      '',
      '',
      enterprise?.id ?? '',
      pageNum,
      PAGE_SIZE,
      true,
      1,
      false,
      undefined
    );
    if (res instanceof GetProjectFilesResponse) {
      const videoFiles = res.getProjectFilesList().filter((f) => f.getType().startsWith('video/'));
      setVideos((prev) => replace ? videoFiles : [...prev, ...videoFiles]);
      setIsScrollEnabled(videoFiles.length >= PAGE_SIZE);
    }
    setIsLoading(false);
  }, [getValidAccessToken, enterprise?.id, setIsScrollEnabled]);

  useEffect(() => {
    setPage(0);
    setVideos([]);
    fetchVideos(0, true);
  }, [fetchVideos, setPage]);

  useEffect(() => {
    if (page > 0) fetchVideos(page);
  }, [page, fetchVideos]);

  useEffect(() => {
    if (isFilesUpdated) {
      setPage(0);
      setVideos([]);
      fetchVideos(0, true).then(() => {
        if (setIsFilesUpdated) setIsFilesUpdated(false);
      });
    }
  }, [isFilesUpdated, fetchVideos, setIsFilesUpdated, setPage]);

  const handleProgress = (pNum: number, idx: number, filesList: any[]) => {
    filesList[idx].uploadProgress = Math.floor(pNum);
    const modifiedList = [...filesList];
    setUploadingFilesDetailsList(modifiedList);
  };

  const processFilesInBatches = async (
    files: File[],
    filesList: any[],
    userId: string,
    awsAccessKeyId: string,
    awsSecretAccessKey: string,
    awsSessionToken: string
  ) => {
    const allKeys: string[] = [];
    const totalBatches = Math.ceil(files.length / 10);

    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const start = batchIndex * 10;
      const end = Math.min(start + 10, files.length);
      const batchFiles = files.slice(start, end);
      const batchPromises: Promise<string>[] = [];

      for (let i = 0; i < batchFiles.length; i++) {
        const file = batchFiles[i];
        const globalIndex = start + i;

        const uploadPromise = awaitableUploadToS3(
          userId,
          file,
          `project-file`,
          file.type,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          (progress) => handleProgress(progress, globalIndex, filesList)
        );

        batchPromises.push(uploadPromise);
      }

      const batchKeys = await Promise.all(batchPromises);
      allKeys.push(...batchKeys);
    }

    return allKeys;
  };

  const handleFiles = async (files: File[]) => {
    const videoFiles = files.filter(isVideoFile);
    if (videoFiles.length === 0) {
      toast.error('Please upload valid video files (mp4, mov, avi, mkv, webm, etc.)');
      return;
    }

    const filesList = videoFiles.map((file) => ({
      file,
      name: file.name,
      uploadProgress: 0,
      isCardOpen: true,
    }));
    setUploadingFilesDetailsList(filesList);
    setUploadingCardsStatusList(
      videoFiles.map((file) => ({
        name: file.name,
        isCardClosed: false,
      }))
    );

    setIsLocalUploading(true);
    setIsUploading(true);

    try {
      if (!awsAccessKeyId || !awsSecretAccessKey || !awsSessionToken) {
        toast.error('AWS credentials are missing. Please try again.');
        setIsLocalUploading(false);
        setIsUploading(false);
        return;
      }

      const normalizedFiles = videoFiles.map((file) => {
        const normalizedName = file.name.replace(/[^\w\s.-]/g, '_');
        return new File([file], normalizedName, { type: file.type });
      });

      const keys = await processFilesInBatches(
        normalizedFiles,
        filesList,
        userId,
        awsAccessKeyId,
        awsSecretAccessKey,
        awsSessionToken
      );

      const projectFiles: ProjectFile[] = [];

      for (let idx = 0; idx < keys.length; idx++) {
        const file = normalizedFiles[idx];
        const key = keys[idx];
        const normalizedName = file.name;

        const pf = new ProjectFile();
        pf.setFileName(normalizedName);
        pf.setFileUrl(`${ASSETS_URL}/${key}`);
        pf.setFileState(FileStateReverseMap[FileState.FILE_STATE_ACTIVE]);
        pf.setFileSize(file.size);
        pf.setOwnerId(userId);
        pf.setProjectId(projectId);
        pf.setEnterpriseId(enterprise?.id ?? '');
        pf.setType(file.type);
        projectFiles.push(pf);

        Mixpanel.track('Video Uploaded', { fileName: normalizedName });
      }

      const token = await getValidAccessToken();
      const res = await postProjectFiles(token, projectFiles);
      if (res instanceof RpcError) throw res;

      toast.success(
        `${videoFiles.length} video${videoFiles.length > 1 ? 's' : ''} uploaded successfully`
      );
      if (setIsCountUpdated) setIsCountUpdated(true);
      await fetchVideos();
      await getStorageUsed();
    } catch (err) {
      console.error('Video upload error:', err);
      toast.error('Failed to upload video(s). Please try again.');
    } finally {
      setIsLocalUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  return (
    <Box sx={{ padding: '16px 0' }}>
      {/* Drag & Drop Zone */}
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isLocalUploading && fileInputRef.current?.click()}
        sx={{
          border: `2px dashed ${isDragging ? '#27B6CB' : BORDER_COLOR}`,
          borderRadius: '12px',
          padding: '40px 24px',
          textAlign: 'center',
          cursor: isLocalUploading ? 'not-allowed' : 'pointer',
          backgroundColor: isDragging ? 'rgba(39, 182, 203, 0.06)' : 'transparent',
          transition: 'border-color 0.2s, background-color 0.2s',
          mb: 3,
          '&:hover': {
            borderColor: isLocalUploading ? BORDER_COLOR : '#27B6CB',
            backgroundColor: isLocalUploading ? 'transparent' : 'rgba(39, 182, 203, 0.04)',
          },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleInputChange}
        />
        <CloudUploadIcon sx={{ fontSize: 48, color: '#27B6CB', mb: 1 }} />
        <Typography sx={{ color: TEXT_COLOR, fontWeight: 'bold', mb: 0.5 }}>
          {isDragging ? 'Drop videos here' : 'Drag & drop videos here'}
        </Typography>
        <Typography sx={{ color: TEXT_COLOR, opacity: 0.6, fontSize: '0.85rem' }}>
          or click to browse &mdash; mp4, mov, avi, mkv, webm supported
        </Typography>
      </Box>

      {/* Previewer modal */}
      <Previewer
        fileUrl={selectedFileToPreview?.getFileUrl() ?? ''}
        fileType={getPreviewType(selectedFileToPreview?.getType() ?? '')}
        isPreviewModalOpen={isPreviewModalOpen}
        toggle={() => setIsPreviewModalOpen(!isPreviewModalOpen)}
      />

      {/* Video List */}
      {isLoading && videos.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress size={32} sx={{ color: '#27B6CB' }} />
        </Box>
      ) : videos.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4, opacity: 0.5 }}>
          <VideocamIcon sx={{ fontSize: 40, color: TEXT_COLOR, mb: 1 }} />
          <Typography sx={{ color: TEXT_COLOR }}>No videos uploaded yet</Typography>
        </Box>
      ) : (
        <Box>
          <Box sx={projectPageStyles.titlesContainer}>
            <Box sx={{ width: '400px' }}>Name</Box>
            <Box sx={{ width: '100px' }}>Type</Box>
            <Box sx={{ width: '100px' }}>Created</Box>
            <Box sx={{ width: '100px' }}>Updated</Box>
            <Box sx={{ width: '100px' }}>Size</Box>
            <Box sx={{ width: '200px' }}>Actions</Box>
          </Box>
          <Box
            ref={scrollContainerRef}
            sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 360px)', pb: 4 }}
          >
            {videos.map((video, i) => (
              <ProjectFileComponent
                key={i}
                projectFile={video}
                session={undefined}
                setInbox={() => {}}
                getProjectFiles={() => fetchVideos(0, true)}
                setProjectFiles={setVideos}
                allowDownload={true}
                setIsPreviewModalOpen={setIsPreviewModalOpen}
                setSelectedFileToPreview={setSelectedFileToPreview}
                getStorageUsed={getStorageUsed}
                shareType={ShareType.SHARE_TYPE_TRACK_FILE}
              />
            ))}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} sx={{ color: '#27B6CB' }} />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};
